import { lazy, Suspense, useDeferredValue, useEffect, useMemo, useRef, useState, type TouchEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IconResult, InventoryItem, SessionUser } from '../../shared/types';
import { api, ApiError } from '../api';
import { inventoryStats, makeFuse, selectItems, type StockFilter, type StockSort } from '../inventory';
import { LogOutIcon, RefreshIcon, SearchIcon, WifiOffIcon } from '../icons';
import { useStockController } from '../useStockController';
import { ProductCard } from './ProductCard';

const ProductDetail = lazy(() => import('./ProductDetail'));

type Props = { user: SessionUser; offline: boolean };
type Toast = { id: number; message: string; kind: 'success' | 'error' };

const filters: Array<{ value: StockFilter; label: string }> = [
  { value: 'all', label: 'All' }, { value: 'in', label: 'In stock' }, { value: 'low', label: 'Low (1–5)' },
  { value: 'out', label: 'Out of stock' }, { value: 'stale', label: 'Stale 14d' },
];

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function Dashboard({ user, offline }: Props) {
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [filter, setFilter] = useState<StockFilter>('all');
  const [sort, setSort] = useState<StockSort>('name-asc');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [iconsEnabled, setIconsEnabled] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStart = useRef(0);

  const inventory = useQuery({ queryKey: ['inventory'], queryFn: () => api.inventory(false) });
  const logout = useMutation({
    mutationFn: api.logout,
    onSettled() {
      queryClient.setQueryData(['session'], null);
      queryClient.removeQueries({ queryKey: ['inventory'] });
    },
  });

  function notify(message: string, kind: 'success' | 'error' = 'success') {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current.slice(-2), { id, message, kind }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4_500);
  }
  const stock = useStockController(notify);
  const activeLocations = useMemo(() => inventory.data?.locations.filter((location) => location.active) ?? [], [inventory.data?.locations]);

  useEffect(() => {
    if (activeLocations.length === 1 && activeLocations[0]) setLocationId(activeLocations[0].id);
    if (locationId && !activeLocations.some((location) => location.id === locationId)) setLocationId(null);
  }, [activeLocations, locationId]);

  useEffect(() => {
    const timer = window.setTimeout(() => setIconsEnabled(true), 800);
    return () => window.clearTimeout(timer);
  }, [inventory.data?.fetchedAt]);

  useEffect(() => {
    if (inventory.error instanceof ApiError && inventory.error.status === 401) {
      queryClient.setQueryData(['session'], null);
    }
  }, [inventory.error, queryClient]);

  useEffect(() => {
    function onPopState() { setSelectedId(null); }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const iconItems = useMemo(() => inventory.data?.items.map((item) => ({
    itemId: item.id,
    name: item.name,
    ...(item.categoryName ? { category: item.categoryName } : {}),
  })) ?? [], [inventory.data?.items]);
  const iconQuery = useQuery({
    queryKey: ['icons', inventory.data?.fetchedAt],
    queryFn: () => api.icons(iconItems),
    enabled: iconsEnabled && iconItems.length > 0 && !offline,
    staleTime: 24 * 60 * 60 * 1_000,
    retry: false,
  });
  const icons = useMemo(() => new Map<string, IconResult>(iconQuery.data?.icons.map((icon) => [icon.itemId, icon]) ?? []), [iconQuery.data]);
  const fuse = useMemo(() => makeFuse(inventory.data?.items ?? []), [inventory.data?.items]);
  const visibleItems = useMemo(() => selectItems(inventory.data?.items ?? [], fuse, deferredSearch, filter, sort, locationId), [deferredSearch, filter, fuse, inventory.data?.items, locationId, sort]);
  const stats = useMemo(() => inventoryStats(inventory.data?.items ?? [], locationId), [inventory.data?.items, locationId]);
  const selectedItem = inventory.data?.items.find((item) => item.id === selectedId) ?? null;

  function handleLocation(event: React.ChangeEvent<HTMLSelectElement>) { setLocationId(event.target.value || null); }
  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) { setSearch(event.target.value); }
  function clearSearch() { setSearch(''); }
  function handleSort(event: React.ChangeEvent<HTMLSelectElement>) { setSort(event.target.value as StockSort); }
  function handleFilter(event: React.MouseEvent<HTMLButtonElement>) { setFilter(event.currentTarget.dataset.filter as StockFilter); }
  function handleLogout() { logout.mutate(); }
  function openItem(item: InventoryItem) {
    setSelectedId(item.id);
    window.history.pushState({ sproutDetail: item.id }, '', `#item-${encodeURIComponent(item.id)}`);
  }
  function closeItem() {
    setSelectedId(null);
    if (window.history.state?.sproutDetail) window.history.back();
    else window.history.replaceState(null, '', window.location.pathname);
  }
  async function refresh() {
    if (offline) { notify('Reconnect to refresh inventory.', 'error'); return; }
    try {
      await queryClient.fetchQuery({ queryKey: ['inventory'], queryFn: () => api.inventory(true), staleTime: 0 });
      notify('Inventory is up to date.');
    } catch { notify('Inventory could not be refreshed.', 'error'); }
  }
  function handleRefresh() { void refresh(); }
  function handleTouchStart(event: TouchEvent) {
    if (window.scrollY === 0) touchStart.current = event.touches[0]?.clientY ?? 0;
  }
  function handleTouchMove(event: TouchEvent) {
    if (!touchStart.current || window.scrollY > 0) return;
    setPullDistance(Math.min(100, Math.max(0, (event.touches[0]?.clientY ?? 0) - touchStart.current)));
  }
  function handleTouchEnd() {
    if (pullDistance >= 72) void refresh();
    setPullDistance(0); touchStart.current = 0;
  }

  return (
    <main className="app-shell" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <a className="skip-link" href="#inventory-list">Skip to inventory</a>
      <div className={`connection-bar ${offline ? 'visible' : ''}`} role="status"><WifiOffIcon /> Offline · stock changes are paused</div>
      <div className="pull-status" style={{ height: `${pullDistance * 0.45}px` }} aria-hidden="true">{pullDistance >= 72 ? 'Release to refresh' : 'Pull to refresh'}</div>
      <header className="app-header">
        <div className="header-brand"><span className="brand-mark small-mark">S</span><span>Sprout &amp; About</span></div>
        <div className="header-actions">
          <button type="button" className="icon-button header-button" onClick={handleRefresh} disabled={inventory.isFetching || offline} aria-label="Refresh inventory"><RefreshIcon className={inventory.isFetching ? 'spinning' : ''} /></button>
          <button type="button" className="icon-button header-button" onClick={handleLogout} disabled={logout.isPending} aria-label="Sign out"><LogOutIcon /></button>
        </div>
      </header>

      <section className="dashboard-head">
        <div><p className="greeting">{greeting()}, {user.username}</p><h1>Inventory garden</h1><p className="lede">See what’s flourishing and what needs attention.</p></div>
        <label className="location-picker"><span>Viewing stock at</span><select value={locationId ?? ''} onChange={handleLocation} aria-label="Stock location">
          {activeLocations.length !== 1 && <option value="">All locations · view only</option>}
          {activeLocations.map((location) => <option value={location.id} key={location.id}>{location.name}</option>)}
        </select></label>
      </section>

      {inventory.data?.partial && <div className="notice warning" role="status">Some Square data could not be loaded. Counts shown may be incomplete.</div>}
      {inventory.data?.stale && <div className="notice warning" role="status">Showing the most recent saved snapshot while Square catches up.</div>}
      {inventory.data?.warnings.map((warning) => <div className="notice subtle" role="status" key={warning}>{warning}</div>)}
      {!locationId && activeLocations.length > 1 && <div className="notice location-notice" role="status">Choose a specific location to enable stock controls.</div>}

      <section className="stats-grid" aria-label="Inventory summary">
        <article><span>Products</span><strong>{inventory.isPending ? '—' : stats.products.toLocaleString()}</strong><i className="stat-sprig" /></article>
        <article><span>Units in stock</span><strong>{inventory.isPending ? '—' : stats.stock.toLocaleString()}</strong><small>{locationId ? 'at this location' : 'across all locations'}</small></article>
        <article className="attention"><span>Running low</span><strong>{inventory.isPending ? '—' : stats.low.toLocaleString()}</strong><small>1–5 units</small></article>
        <article className="urgent"><span>Out of stock</span><strong>{inventory.isPending ? '—' : stats.out.toLocaleString()}</strong><small>needs attention</small></article>
      </section>

      <section className="inventory-panel" aria-labelledby="inventory-heading">
        <div className="toolbar-heading"><div><span className="eyebrow">Your catalogue</span><h2 id="inventory-heading">Products</h2></div><span className="result-count" aria-live="polite">{visibleItems.length} shown</span></div>
        <div className="toolbar">
          <label className="search-box"><SearchIcon /><span className="sr-only">Search products</span><input type="search" placeholder="Search name, SKU or variation…" value={search} onChange={handleSearch} />{search && <button type="button" onClick={clearSearch}>Clear</button>}</label>
          <label className="sort-box"><span className="sr-only">Sort inventory</span><select value={sort} onChange={handleSort}>
            <option value="name-asc">Name A–Z</option><option value="name-desc">Name Z–A</option><option value="stock-asc">Stock: low first</option><option value="stock-desc">Stock: high first</option><option value="updated-asc">Oldest updated</option><option value="updated-desc">Recently updated</option>
          </select></label>
        </div>
        <div className="filter-row" role="group" aria-label="Filter inventory">
          {filters.map((entry) => <button type="button" key={entry.value} data-filter={entry.value} aria-pressed={filter === entry.value} onClick={handleFilter}>{entry.label}</button>)}
        </div>

        {inventory.isPending && <SkeletonList />}
        {inventory.isError && !inventory.data && <ErrorState onRetry={handleRefresh} offline={offline} />}
        {!inventory.isPending && !inventory.isError && visibleItems.length === 0 && <EmptyState searching={Boolean(search) || filter !== 'all'} onClear={() => { setSearch(''); setFilter('all'); }} />}
        {visibleItems.length > 0 && <div className="product-grid" id="inventory-list">
          {visibleItems.map((item) => <ProductCard key={item.id} item={item} locationId={locationId} icon={icons.get(item.id)} offline={offline} onOpen={openItem} onAdjust={stock.adjust} />)}
        </div>}
      </section>

      {selectedItem && <Suspense fallback={<div className="drawer-loading" role="status">Opening product…</div>}><ProductDetail item={selectedItem} locationId={locationId} locationName={activeLocations.find((location) => location.id === locationId)?.name ?? 'All locations'} icon={icons.get(selectedItem.id)} offline={offline} onClose={closeItem} onAdjust={stock.adjust} onSetStock={stock.setStock} onUpdateItem={stock.updateItem} /></Suspense>}
      <div className="toast-stack" aria-live="polite">{toasts.map((toast) => <div className={`app-toast ${toast.kind}`} key={toast.id}>{toast.message}</div>)}</div>
    </main>
  );
}

function SkeletonList() {
  return <div className="product-grid" aria-busy="true" aria-label="Loading inventory">{Array.from({ length: 8 }, (_, index) => <div className="product-card skeleton-card" key={index}><i /><span /><span /></div>)}</div>;
}

function ErrorState({ onRetry, offline }: { onRetry: () => void; offline: boolean }) {
  return <div className="state-card" role="alert"><span className="state-illustration">🌧️</span><h3>{offline ? 'You’re offline' : 'Inventory didn’t load'}</h3><p>{offline ? 'Reconnect to retrieve your inventory.' : 'Nothing has been changed. Try the connection again.'}</p><button type="button" className="secondary-button" onClick={onRetry} disabled={offline}>Try again</button></div>;
}

function EmptyState({ searching, onClear }: { searching: boolean; onClear: () => void }) {
  return <div className="state-card"><span className="state-illustration">{searching ? '🔎' : '🌱'}</span><h3>{searching ? 'No products match' : 'Ready for your first product'}</h3><p>{searching ? 'Try a different search or clear the filters.' : 'Products from Square will appear here.'}</p>{searching && <button type="button" className="secondary-button" onClick={onClear}>Clear search and filters</button>}</div>;
}
